// frontend/user/src/_lib/upload-store.ts
//
// 跨页面共享上传任务状态(zustand)+ IndexedDB 持久化(刷新保留列表)。
// 设计来源:docs/superpowers/plans/2026-06-07-transfers-persist-and-speed.md 任务 3。
//
// 持久化:状态变更写 IDB(toPersistable 剥离 File / speedBps);
// 启动 hydrate() rehydrate(uploading/pending → interrupted)。speedBps 瞬时态不持久化。

import { create } from 'zustand'
import {
  type AnyTask,
  type UploadTaskStatus,
  loadPersistedTasks,
  putPersistedTask,
  deletePersistedTask,
  deletePersistedWhere,
  persistAvailable,
} from './transfer-persist'

export type { UploadTaskStatus }
export type UploadTask = AnyTask

type UploadStore = {
  tasks: UploadTask[]
  hydrated: boolean
  hydrate: () => Promise<void>
  addTask: (t: UploadTask) => void
  updateTask: (id: string, patch: Partial<UploadTask>) => void
  removeTask: (id: string) => void
  clearDone: () => void
  clearFinished: () => void
  clearInactive: () => void
  resetForRetry: (id: string) => UploadTask | undefined
}

export const useUploadStore = create<UploadStore>((set, get) => ({
  tasks: [],
  hydrated: false,
  hydrate: async () => {
    if (get().hydrated) return
    const persisted = await loadPersistedTasks()
    set((s) => {
      // 合并:本次会话已在内存的任务优先,不被持久化快照覆盖。
      const liveIds = new Set(s.tasks.map((t) => t.id))
      const merged = [...s.tasks, ...persisted.filter((p) => !liveIds.has(p.id))]
      return { tasks: merged, hydrated: true }
    })
  },
  addTask: (t) => {
    set((s) => ({ tasks: [...s.tasks, t] }))
    void putPersistedTask(t)
  },
  updateTask: (id, patch) => {
    let updated: UploadTask | undefined
    set((s) => ({
      tasks: s.tasks.map((t) => {
        if (t.id !== id) return t
        updated = { ...t, ...patch }
        return updated
      }),
    }))
    // 纯进度/速度的高频 patch 不写库(瞬时态);状态切换等才落库。
    if (updated && !isProgressOnlyPatch(patch)) void putPersistedTask(updated)
  },
  removeTask: (id) => {
    set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) }))
    void deletePersistedTask(id)
  },
  clearDone: () => {
    set((s) => ({ tasks: s.tasks.filter((t) => t.status !== 'done') }))
    void deletePersistedWhere((t) => t.status === 'done')
  },
  clearFinished: () => {
    set((s) => ({ tasks: s.tasks.filter((t) => t.status !== 'done' && t.status !== 'error') }))
    void deletePersistedWhere((t) => t.status === 'done' || t.status === 'error')
  },
  clearInactive: () => {
    // 删除按钮:清掉除「正在上传」(pending/uploading)外的所有任务(paused/done/error/interrupted)。
    // 被清的 paused 无活动 controller(pauseUpload 已 delete),无需 abort。
    set((s) => ({ tasks: s.tasks.filter((t) => t.status === 'pending' || t.status === 'uploading') }))
    void deletePersistedWhere((t) => t.status !== 'pending' && t.status !== 'uploading')
  },
  resetForRetry: (id) => {
    const cur = get().tasks.find((t) => t.id === id)
    if (!cur) return undefined
    set((s) => ({
      tasks: s.tasks.map((t) => {
        if (t.id === id) {
          return { ...t, status: 'pending' as const, percent: 0, bytesUploaded: 0, speedBps: undefined, error: undefined }
        }
        return t
      }),
    }))
    const next = get().tasks.find((t) => t.id === id)
    if (next) void putPersistedTask(next)
    return cur
  },
}))

// 瞬时进度 patch → 跳过 IDB 写(避免高频 IO)。判定:只动 bytesUploaded/percent/speedBps/status,
// 且 status 未变或仍是 'uploading'。终态(done/error/interrupted)与 'pending' 仍落库,
// 因 upload-run 的 onProgress 每帧都带 status:'uploading',不能仅凭"有 status"就写库。
function isProgressOnlyPatch(patch: Partial<UploadTask>): boolean {
  const keys = Object.keys(patch)
  const onlyProgressKeys = keys.every(
    (k) =>
      k === 'speedBps' ||
      k === 'bytesUploaded' ||
      k === 'percent' ||
      k === 'status' ||
      k === 'hashPercent',
  )
  if (!onlyProgressKeys) return false
  return patch.status === undefined || patch.status === 'uploading'
}

// 浏览器侧自动 rehydrate(jsdom/SSR 无 indexedDB 时跳过)。
if (persistAvailable()) {
  void useUploadStore.getState().hydrate()
}
